/* eslint-disable no-unused-vars, i18next/no-literal-string */
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Text } from 'rebass';
import { Flex } from 'rebass/styled-components';

import useTranslation from 'hooks/use-translation';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import useUser from 'hooks/use-user';

import { Button } from 'components/core';
import { DutchiePayLogo } from 'assets/dutchie-pay-logo';
import SecureBriefcaseIcon from 'shared/assets/secure-briefcase-icon';
import { tracker } from 'utils/analytics';
import useUI from 'hooks/use-ui';
import useFeatureFlags from 'hooks/use-feature-flags';
import { useRouter } from 'next/router';
import useLogRocket from 'src/hooks/use-logrocket';
import { ModalNames, useModals } from 'src/components/modals';

export type PayButtonPosition = 'bottom' | 'top';

type ButtonProps = {
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  inverted?: boolean;
  link?: boolean;
  onClick: () => () => void;
};

export type PaySignUpButtonProps = {
  textPosition?: PayButtonPosition;
};

type SecureTextProps = {
  textPosition: string;
};

const LearnMoreButton = (): JSX.Element => {
  const FeatureFlags = useFeatureFlags();
  const { track } = useLogRocket();
  const { openModal } = useModals();

  const handleOpenDutchiePayCtaModal = useCallback(async (): Promise<boolean> => {
    tracker.dutchiePayLearnMoreButtonClicked();
    // TODO: Setup LogRocket in the FE Analytics framework https://dutchie.atlassian.net/browse/ENG-31004
    track(`DutchiePay Learn More Button Clicked`);

    return openModal(ModalNames.dutchiePayCta);
  }, [openModal, track]);

  const learnMoreEnabled = FeatureFlags.flags['rollout.dutchie-pay.cta-modal'];
  if (learnMoreEnabled) {
    return <LearnMore onClick={handleOpenDutchiePayCtaModal}>Learn more</LearnMore>;
  }
  return <></>;
};

const SecureText = ({ textPosition }: SecureTextProps): JSX.Element => {
  const { Trans } = useTranslation();

  return (
    <SecureTextWrapper textPosition={textPosition}>
      <Flex alignItems='center' justifyContent='center'>
        <SecureBriefcaseIcon />
        <Text marginLeft='6px'>
          <Trans i18nKey='payment.payments-cannabis'>
            <strong>Secure</strong>&nbsp;and simple digital payments. <LearnMoreButton />
          </Trans>
        </Text>
      </Flex>
    </SecureTextWrapper>
  );
};

export const PaySignUpButton = ({ textPosition = `bottom` }: PaySignUpButtonProps): JSX.Element => {
  const User = useUser();
  const UI = useUI();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const FeatureFlags = useFeatureFlags();
  const router = useRouter();
  const dutchiePayPageUiEnabled = FeatureFlags.flags['rollout.dutchie-pay-page-ui'];
  const { track } = useLogRocket();

  const handleClick = (): void => {
    if (!dutchiePayPageUiEnabled) {
      DutchiePayEnrollment.beginEnrollment(User);
      UI.openDutchiePaySignUpModal();
    } else {
      const query = UI.variant === 'plus' ? { refUrl: router.asPath } : null;

      void router.push({
        pathname: '/dutchiepay',
        query,
      });
    }
    tracker.dutchiePayEnrollmentButtonClicked();
    // TODO: Setup LogRocket in the FE Analytics framework https://dutchie.atlassian.net/browse/ENG-31004
    track(`DutchiePay_enrolling`);
  };

  return (
    <>
      {textPosition === `top` && <SecureText textPosition={textPosition} />}
      <DutchiePayButton onClick={handleClick} aria-label='dutchiePay'>
        <DutchiePayLogo width='108' height='28' />
      </DutchiePayButton>
      {textPosition !== `top` && <SecureText textPosition={textPosition} />}
    </>
  );
};

const DutchiePayButton = styled(({ ...props }: ButtonProps) => <Button {...props} />)`
  width: 100%;
  height: 45px;
  background-color: ${({ theme }) => theme.colors.dutchiePayGreen};
  font: normal 22px/27px Matter, proxima-nova, sans-serif;
  text-transform: none;

  &&& {
    padding: 10px 15px;

    svg {
      margin-right: 0;
      vertical-align: baseline;
    }
  }
`;

const SecureTextWrapper = styled.div`
  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin-left: -2px;
    margin-right: -2px;
  }

  ${(props) => (props.textPosition === `top` ? `margin-bottom: 15px;` : `margin-top: 15px;`)}
  color: ${({ theme }) => theme.colors.grey[45]};
  font-size: 12px;
`;

const LearnMore = styled.button`
  color: ${({ theme }) => theme.colors.primaryBlue};
  background: transparent;
  border: none;
  margin: 0;
  padding: 0;
  font-size: 12px;
  cursor: pointer;
`;
